/**
 * Builder Page - Wrapper for the existing URDF builder
 */

import { useParams, useNavigate } from "react-router-dom";
import { useEffect } from "react";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { App } from "../App";
import { WebSocketProvider } from "../WebSocket";
import { usePageView } from "../hooks/usePageView";

/**
 * Generate a random session ID
 */
function generateSessionId(): string {
  const chars = "abcdefghijklmnopqrstuvwxyz0123456789";
  let result = "";
  for (let i = 0; i < 12; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
}

/**
 * Get WebSocket URL for the session
 */
function getWebSocketUrl(sessionId: string): string {
  const params = new URLSearchParams(window.location.search);
  const wsParam = params.get("ws");
  // Live AR hand-off ticket: coming back from the App Clip there are no
  // Safari cookies, so this is what proves the session is still ours.
  const ticket = params.get("ar-ticket");
  const ticketQuery = ticket ? `ticket=${encodeURIComponent(ticket)}` : "";

  if (wsParam) {
    const separator = wsParam.includes("?") ? "&" : "?";
    const base = `${wsParam}${separator}session=${sessionId}`;
    return ticketQuery ? `${base}&${ticketQuery}` : base;
  }

  const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
  const port = window.location.port || (protocol === "wss:" ? "443" : "80");
  const base = `${protocol}//${window.location.hostname}:${port}/session/${sessionId}`;
  return ticketQuery ? `${base}?${ticketQuery}` : base;
}

export function BuilderPage() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();

  usePageView(sessionId);

  // If no session ID provided, generate one and redirect
  useEffect(() => {
    if (!sessionId) {
      const newSessionId = generateSessionId();
      navigate(`/builder/${newSessionId}`, { replace: true });
    }
  }, [sessionId, navigate]);

  // Don't render until we have a valid session ID
  if (!sessionId) {
    return null;
  }

  // Store session ID in window for access by components
  window.sessionId = sessionId;
  const wsUrl = getWebSocketUrl(sessionId);

  console.log("Session ID:", sessionId);
  console.log("Connecting to WebSocket:", wsUrl);

  return (
    <DndProvider backend={HTML5Backend}>
      <WebSocketProvider url={wsUrl}>
        <App />
      </WebSocketProvider>
    </DndProvider>
  );
}
